import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Upload from '@mui/icons-material/Upload';
import { openUrlAndDownload } from '../../helpers';
import { shouldShowDocumentAsReadyButDisabled } from '../../helpers/document-platform-config';

const DocumentChip = ({
  documentToUpload,
  inputRef,
  document,
  handleChange,
  loading,
  handleOpenInput,
  url,
  allowedToUploadDocument,
  company,
  requestingPlatform,
}) => {
  const hasDocument = !!url;
  const isContract = document.documentType === 'contract';
  const companyDocument = document?.companyDocument;
  const hasRealPdfDocument = !!(companyDocument?.id && companyDocument?.lastFile && url);
  const shouldShowAsReadyButDisabled = shouldShowDocumentAsReadyButDisabled(
    requestingPlatform,
    isContract ? 'contract' : null,
    company,
    hasRealPdfDocument,
  );

  const isLoading = useMemo(
    () => documentToUpload?.documentType === document.documentType && loading,
    [documentToUpload, document, loading],
  );

  const handleClick = () => {
    if (shouldShowAsReadyButDisabled) {
      return;
    }
    if (document.documentType === 'OTHER_DOCUMENTS') {
      document.handleClick();
    } else if (url !== '') {
      openUrlAndDownload(url);
    } else if (allowedToUploadDocument) {
      handleOpenInput(document);
    }
  };

  const isChipReady = shouldShowAsReadyButDisabled || hasDocument;
  const chipColor = isChipReady ? 'primary' : 'default';
  const chipVariant = isChipReady ? 'filled' : 'outlined';
  const chipDisabled = shouldShowAsReadyButDisabled;

  const deleteIcon = useMemo(() => {
    if (chipDisabled) return undefined;
    return isLoading ? <CircularProgress size={12} /> : <Upload />;
  }, [chipDisabled, isLoading]);

  const chipProps = {
    size: 'small',
    label: document.label,
    id: document.documentType.toLowerCase(),
    color: chipColor,
    variant: chipVariant,
    onClick: handleClick,
    disabled: chipDisabled,
    sx: { '& .MuiChip-label': { fontSize: 11 }, marginInline: 0.2, marginTop: 0.5 },
  };

  if (!allowedToUploadDocument) {
    return (
      <Chip {...chipProps} />
    );
  }

  return (
    <>
      <input type="file" hidden ref={inputRef} onChange={(e) => handleChange(e, document)} />
      <Chip
        {...chipProps}
        onDelete={chipDisabled ? undefined : () => handleOpenInput(document)}
        deleteIcon={deleteIcon}
      />
    </>
  );
};

DocumentChip.propTypes = {
  documentToUpload: PropTypes.shape({
    documentType: PropTypes.string,
  }),
  document: PropTypes.shape({
    label: PropTypes.string,
    documentType: PropTypes.string,
    handleClick: PropTypes.func,
    companyDocument: PropTypes.shape({
      id: PropTypes.string.isRequired,
      globalAppId: PropTypes.string.isRequired,
    }),
  }).isRequired,
  handleChange: PropTypes.func,
  loading: PropTypes.bool,
  handleOpenInput: PropTypes.func,
  inputRef: PropTypes.shape({}),
  url: PropTypes.string,
  allowedToUploadDocument: PropTypes.bool,
  company: PropTypes.shape({
    hasFrameworkContract: PropTypes.bool,
  }),
  requestingPlatform: PropTypes.string,
};

DocumentChip.defaultProps = {
  url: null,
  documentToUpload: { documentType: '' },
  handleChange: () => {},
  handleOpenInput: () => {},
  loading: false,
  inputRef: null,
  allowedToUploadDocument: true,
  company: null,
  requestingPlatform: null,
};

export default DocumentChip;
